import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { Subject, firstValueFrom } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { QcEvent, QcRecord } from '../domain/qc.model';
import { QcExpandedPanelDialogComponent } from './qc-expanded-panel-dialog/qc-expanded-panel-dialog.component';
import { CollectorUser } from '../domain/user';
import { AlertService } from '../services/alert.service';
import { CollectorProfileService } from '../services/collector-profile.service';
import { QcService } from '../services/qc.service';
import { UserService } from '../services/user.service';

const INITIAL_PAGE_SIZE = 20;
const PAGE_SIZE_OPTIONS = [10, 20, 50];
const EXCLUDED_EVENT_TYPE_CODES = ['GS', 'GO', 'GM'];

@Component({
  selector: 'app-qc',
  templateUrl: './qc.component.html',
  styleUrls: ['./qc.component.css']
})
export class QcComponent implements OnInit, OnDestroy {
  readonly displayedColumns = [
    'actions',
    'dcType',
    'league',
    'dcDate',
    'gameDate',
    'game',
    'time',
    'event',
    'collector',
    'status'
  ];
  readonly pageSizeOptions = PAGE_SIZE_OPTIONS;

  events: QcEvent[] = [];
  collectorDirectory = new Map<string, CollectorUser>();
  eventCodeOptions: string[] = [];

  loading = false;

  totalCount = 0;
  pageIndex = 0;
  pageSize = INITIAL_PAGE_SIZE;

  leagueFilter = '';
  eventCodeFilter = '';
  collectorAuthIdFilter = '';
  gameFilter = '';
  dcDateFromFilter = this.buildDateFilterWithDayOffset(-30);
  dcDateToFilter = this.buildDateFilterWithDayOffset(30);
  showAnalyzed = true;

  private readonly textFiltersChange$ = new Subject<string>();
  private readonly dcDateFiltersChange$ = new Subject<string>();
  private readonly destroy$ = new Subject<void>();

  constructor(
    private qcService: QcService,
    private userService: UserService,
    private alertService: AlertService,
    private collectorProfileService: CollectorProfileService,
    private dialog: MatDialog
  ) {}

  async ngOnInit(): Promise<void> {
    this.textFiltersChange$
      .pipe(debounceTime(500), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(() => {
        void this.applyTextFilters();
      });
    this.dcDateFiltersChange$
      .pipe(debounceTime(500), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(() => {
        void this.applyDcDateFilters();
      });

    await this.loadEventTypeOptions();
    await this.loadEvents();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.textFiltersChange$.complete();
    this.dcDateFiltersChange$.complete();
  }

  get visibleEventCount(): number {
    return this.events.length;
  }

  async onEventCodeFilterChanged(eventCode: string): Promise<void> {
    this.eventCodeFilter = eventCode || '';
    await this.onFiltersChanged();
  }

  onLeagueFilterChanged(league: string): void {
    this.leagueFilter = league || '';
    this.textFiltersChange$.next(this.buildTextFiltersKey());
  }

  async onCollectorFilterChanged(auth0Id: string): Promise<void> {
    this.collectorAuthIdFilter = auth0Id || '';
    this.pageIndex = 0;
    await this.loadEvents();
  }

  onGameFilterChanged(game: string): void {
    this.gameFilter = game || '';
    this.textFiltersChange$.next(this.buildTextFiltersKey());
  }

  onDcDateFromFilterChanged(dcDateFrom: string): void {
    this.dcDateFromFilter = dcDateFrom || '';
    this.normalizeDcDateRange();
    this.dcDateFiltersChange$.next(this.buildDcDateFiltersKey());
  }

  onDcDateToFilterChanged(dcDateTo: string): void {
    this.dcDateToFilter = dcDateTo || '';
    this.normalizeDcDateRange();
    this.dcDateFiltersChange$.next(this.buildDcDateFiltersKey());
  }

  async onShowAnalyzedChanged(showAnalyzed: boolean): Promise<void> {
    this.showAnalyzed = showAnalyzed;
    await this.onFiltersChanged();
  }

  async onPageChanged(event: PageEvent): Promise<void> {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    await this.loadEvents();
  }

  openQcDialog(event: QcEvent): void {
    const dialogRef = this.dialog.open(QcExpandedPanelDialogComponent, {
      width: '88vw',
      height: '80vh',
      maxWidth: '95vw',
      maxHeight: '95vh',
      autoFocus: false,
      panelClass: 'qc-dialog-container',
      data: {
        event,
        collectorName: this.getCollectorName(event)
      }
    });

    const savedSubscription = dialogRef.componentInstance.qcSaved.subscribe(
      (qc) => {
        this.onQcSaved(event, qc, dialogRef);
      }
    );

    dialogRef.afterClosed().subscribe(() => {
      savedSubscription.unsubscribe();
    });
  }

  onQcSaved(
    event: QcEvent,
    qc: QcRecord,
    dialogRef?: MatDialogRef<QcExpandedPanelDialogComponent>
  ): void {
    event.qc = qc;
    if (!this.showAnalyzed && qc.status === 'done') {
      dialogRef?.close();
      if (this.events.length === 1 && this.pageIndex > 0) {
        this.pageIndex -= 1;
      }
      void this.loadEvents();
    }
  }

  getCollectorName(event: QcEvent): string {
    return (
      this.collectorDirectory.get(event.collectorAuthId)?.name ??
      event.collectorAuthId
    );
  }

  getCollectorEmail(event: QcEvent): string {
    return this.collectorDirectory.get(event.collectorAuthId)?.email ?? '—';
  }

  getStatusLabel(event: QcEvent): string {
    return event.qc?.status === 'done' ? 'Done' : '—';
  }

  getGameLabel(event: QcEvent): string {
    return `${event.homeTeam} - ${event.awayTeam}`;
  }

  trackByEventId(_: number, event: QcEvent): string {
    return event._id;
  }

  private buildTextFiltersKey(): string {
    return `${this.leagueFilter.trim()}|${this.gameFilter.trim()}`;
  }

  private buildDcDateFiltersKey(): string {
    return `${this.dcDateFromFilter}|${this.dcDateToFilter}`;
  }

  private async applyTextFilters(): Promise<void> {
    this.pageIndex = 0;
    await this.loadEvents();
  }

  private async applyDcDateFilters(): Promise<void> {
    this.pageIndex = 0;
    await this.loadEvents();
  }

  private async onFiltersChanged(): Promise<void> {
    this.pageIndex = 0;
    await this.loadEvents();
  }

  private async loadEventTypeOptions(): Promise<void> {
    try {
      const config = await firstValueFrom(
        this.collectorProfileService.getConfig()
      );
      this.eventCodeOptions = [...(config?.eventSkillCodes ?? [])]
        .filter((code) => !EXCLUDED_EVENT_TYPE_CODES.includes(code))
        .sort((left, right) => left.localeCompare(right));
      if (
        this.eventCodeFilter &&
        !this.eventCodeOptions.includes(this.eventCodeFilter)
      ) {
        this.eventCodeOptions = [
          this.eventCodeFilter,
          ...this.eventCodeOptions
        ];
      }
    } catch (error) {
      this.eventCodeOptions = [];
      this.alertService.showError(
        'Could not load QC event types: ' + (error?.message ?? 'unknown error')
      );
    }
  }

  private async loadEvents(): Promise<void> {
    try {
      this.loading = true;
      const result = await firstValueFrom(
        this.qcService.listEvents({
          page: this.pageIndex,
          pageSize: this.pageSize,
          league: this.leagueFilter,
          eventCode: this.eventCodeFilter,
          game: this.gameFilter,
          collectorAuthId: this.collectorAuthIdFilter,
          dcDateFrom: this.dcDateFromFilter || null,
          dcDateTo: this.dcDateToFilter || null,
          showAnalyzed: this.showAnalyzed
        })
      );
      this.totalCount = result?.count ?? 0;
      this.events = result?.data ?? [];
      await this.resolveCollectors();
    } catch (error) {
      this.totalCount = 0;
      this.events = [];
      this.collectorDirectory.clear();
      this.alertService.showError(
        'Could not load QC events: ' + (error?.message ?? 'unknown error')
      );
    } finally {
      this.loading = false;
    }
  }

  private normalizeDcDateRange(): void {
    if (
      this.dcDateFromFilter &&
      this.dcDateToFilter &&
      this.dcDateFromFilter > this.dcDateToFilter
    ) {
      [this.dcDateFromFilter, this.dcDateToFilter] = [
        this.dcDateToFilter,
        this.dcDateFromFilter
      ];
    }
  }

  private buildDateFilterWithDayOffset(dayOffset: number): string {
    const date = new Date();
    date.setDate(date.getDate() + dayOffset);
    date.setHours(0, 0, 0, 0);
    return this.formatDateFilter(date);
  }

  private formatDateFilter(value: Date): string {
    return value.toISOString().slice(0, 10);
  }

  private async resolveCollectors(): Promise<void> {
    const auth0Ids = [
      ...new Set(this.events.map((event) => event.collectorAuthId))
    ].filter((value) => !!value);

    if (auth0Ids.length === 0) {
      this.collectorDirectory.clear();
      return;
    }

    try {
      const users = await firstValueFrom(
        this.userService.findByAuth0Ids(auth0Ids)
      );
      this.collectorDirectory = users.reduce((collectorMap, user) => {
        collectorMap.set(user.auth0Id, user);
        return collectorMap;
      }, new Map<string, CollectorUser>());
    } catch (error) {
      this.collectorDirectory.clear();
    }
  }
}
